import React, { useState, useEffect } from 'react';
import { getDashboard } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY_DATA = {
  totalOrders: 0, todayOrders: 0, totalRevenue: 0, totalTax: 0,
  topItems: [], ordersByStatus: {},
};
const HOURLY = { '10am': 0, '11am': 0, '12pm': 0, '1pm': 0, '2pm': 0, '3pm': 0, '4pm': 0, '5pm': 0, '6pm': 0 };

export default function AnalyticsPage() {
  const { vendor } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!vendor?.id) return;
    setLoading(true);
    setError('');
    getDashboard()
      .then((res) => { setData(res.data); setLoading(false); })
      .catch((err) => { console.error('Analytics load failed:', err); setError('Failed to load analytics'); setLoading(false); });
  }, [vendor?.id]);

  const d    = data || EMPTY_DATA;
  const maxQty = Math.max(...(d.topItems || []).map((i) => i.qty), 1);
  const hourly = d.hourly || HOURLY;
  const maxH   = Math.max(...Object.values(hourly), 1);

  return (
    <div className="page">
      <div className="page-title">Analytics Dashboard</div>
      <div className="page-sub">Sales performance overview</div>

      {loading && <div style={{ color:'var(--text3)', padding:'40px 0', textAlign:'center' }}>Loading analytics…</div>}
      {error   && <div style={{ color:'var(--red)', padding:'10px 16px', background:'#3d1a1a', borderRadius:8, marginBottom:16 }}>{error}</div>}

      <div className="grid4" style={{ marginBottom: 24 }}>
        {[
          ['Total Orders', d.totalOrders],
          ['Revenue (AED)', Number(d.totalRevenue || 0).toFixed(0)],
          ["Today's Orders", d.todayOrders],
          ['Tax Collected', `AED ${Number(d.totalTax || 0).toFixed(0)}`],
        ].map(([label, val]) => (
          <div key={label} className="stat-card">
            <div className="stat-label">{label}</div>
            <div className="stat-val">{val}</div>
          </div>
        ))}
      </div>

      <div className="grid2">
        {/* Top items bar chart */}
        <div className="card">
          <div className="section-label" style={{ marginBottom: 8 }}>Top Selling Items</div>
          {(d.topItems || []).length === 0 && !loading && (
            <div style={{ color:'var(--text3)', fontSize:13, padding:'20px 0' }}>No orders yet</div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140 }}>
            {(d.topItems || []).map(({ name, qty }) => (
              <div key={name} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--accent)' }}>{qty}</span>
                <div style={{ width: '100%', background: 'var(--accent)', borderRadius: '4px 4px 0 0', height: `${(qty / maxQty) * 100}%`, minWidth: 24, opacity: .85 }} />
                <span style={{ fontSize: 10, color: 'var(--text2)', textAlign: 'center', maxWidth: 50, wordBreak: 'break-word' }}>{name.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly orders */}
        <div className="card">
          <div className="section-label" style={{ marginBottom: 8 }}>Orders by Hour</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140 }}>
            {Object.entries(hourly).map(([hr, cnt]) => (
              <div key={hr} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--blue)' }}>{cnt}</span>
                <div style={{ width: '100%', background: 'var(--blue)', borderRadius: '4px 4px 0 0', height: `${(cnt / maxH) * 100}%`, minWidth: 24, opacity: .85 }} />
                <span style={{ fontSize: 10, color: 'var(--text2)' }}>{hr}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="section-label" style={{ marginBottom: 12 }}>Orders by Status</div>
        <div className="grid4">
          {Object.entries(d.ordersByStatus || {}).length === 0 && !loading && (
            <div style={{ color:'var(--text3)', fontSize:13 }}>No orders yet</div>
          )}
          {Object.entries(d.ordersByStatus || {}).map(([status, count]) => (
            <div key={status} style={{ textAlign: 'center', padding: 16, background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>{status}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
